import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Card, Loader } from "@/components/Common";

const MEANS = { sostanza_attiva: "Sostanza attiva", mezzo_biologico: "Mezzo biologico", organismo_utile: "Organismo utile", mezzo_fisico: "Mezzo fisico" };
const EMPTY = { adversity_id: "", crop: "Agrumi", active_substance: "", means_type: "sostanza_attiva", organic_allowed: false, organic_reference: "", limitations: "", source_name: "", source_url: "", verified_at: new Date().toISOString().slice(0, 10) };

function Lookup() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const go = async () => setRes((await api.get("/pro/registry/lookup", { params: { substance: q } })).data);
  return (
    <Card testid="registry-lookup">
      <p className="eyebrow">Verifica sostanza nel registro ministeriale</p>
      <div className="flex gap-2">
        <input className="field" placeholder="es. COPPER HYDROXIDE" value={q} onChange={(e) => setQ(e.target.value)} data-testid="registry-lookup-input" />
        <button onClick={go} className="btn-primary !px-4" data-testid="registry-lookup-button"><Search size={16} /></button>
      </div>
      {res && <p className="mt-2 text-sm" data-testid="registry-lookup-result">{res.verified ? `${res.authorized_products} prodotti autorizzati contenenti "${res.substance}" (dataset ${fmtDate(res.dataset_date)})` : "Registro non disponibile"}</p>}
    </Card>
  );
}

export default function ProUses() {
  const [d, setD] = useState(null);
  const [kb, setKb] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(() => api.get("/pro/verified-uses").then(({ data }) => setD(data)), []);
  useEffect(() => { load(); api.get("/pro/kb").then(({ data }) => setKb(data)); }, [load]);
  if (!d) return <Loader />;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const sync = async () => {
    setSyncing(true);
    await api.post("/pro/registry/sync").then(() => toast.success("Registro aggiornato")).catch((e) => toast.error(errMsg(e)));
    setSyncing(false);
    load();
  };
  const add = async () => {
    try {
      await api.post("/pro/verified-uses", f);
      toast.success("Impiego verificato registrato");
      setF(EMPTY);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-4 px-5 py-6" data-testid="pro-uses-page">
      <div>
        <p className="eyebrow">Area riservata</p>
        <h1 className="font-serif text-3xl font-semibold text-[#0D2517]">Impieghi fitosanitari verificati</h1>
        <p className="mt-1 text-xs text-[#5b6b60]">Solo gli impieghi qui registrati, verificati su fonte ufficiale (etichetta autorizzata, BDF Ministero della Salute), vengono mostrati agli utenti con fonte e data. L'AI non genera dosi né autorizzazioni.</p>
      </div>
      <Card testid="registry-meta">
        <p className="eyebrow">Registro prodotti fitosanitari</p>
        {d.registry ? <p className="text-sm">{d.registry.source_name}<br /><span className="text-xs text-[#5b6b60]">{d.registry.rows} prodotti · dataset del {fmtDate(d.registry.dataset_date)} · scaricato il {fmtDate(d.registry.downloaded_at)}</span></p> : <p className="text-sm text-[#7f1d1d]">Registro non ancora importato.</p>}
        <button onClick={sync} disabled={syncing} className="btn-secondary mt-3 w-full !py-2.5" data-testid="registry-sync-button"><RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> Aggiorna ora</button>
      </Card>
      <Lookup />
      <Card testid="verified-use-form" accent>
        <p className="eyebrow">Nuovo impiego verificato</p>
        <select className="field" value={f.adversity_id} onChange={set("adversity_id")} data-testid="use-adversity-select">
          <option value="">Avversità…</option>
          {kb.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
        </select>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <input className="field" placeholder="Coltura" value={f.crop} onChange={set("crop")} data-testid="use-crop" />
          <select className="field" value={f.means_type} onChange={set("means_type")} data-testid="use-means-type">
            {Object.entries(MEANS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <input className="field mt-2" placeholder="Sostanza attiva / mezzo" value={f.active_substance} onChange={set("active_substance")} data-testid="use-substance" />
        <input className="field mt-2" placeholder="Limitazioni pertinenti" value={f.limitations} onChange={set("limitations")} data-testid="use-limitations" />
        <label className="mt-2 flex items-center gap-2 text-sm"><Checkbox checked={f.organic_allowed} onCheckedChange={(v) => setF({ ...f, organic_allowed: !!v })} data-testid="use-organic-checkbox" /> Ammesso in agricoltura biologica</label>
        {f.organic_allowed && <input className="field mt-2" placeholder="Riferimento (es. Reg. UE 2021/1165, All. I)" value={f.organic_reference} onChange={set("organic_reference")} data-testid="use-organic-ref" />}
        <input className="field mt-2" placeholder="Fonte (nome)" value={f.source_name} onChange={set("source_name")} data-testid="use-source-name" />
        <input className="field mt-2" placeholder="Fonte (URL)" value={f.source_url} onChange={set("source_url")} data-testid="use-source-url" />
        <input type="date" className="field mt-2" value={f.verified_at} onChange={set("verified_at")} data-testid="use-verified-at" />
        <button onClick={add} className="btn-primary mt-3 w-full" data-testid="use-save-button">REGISTRA IMPIEGO</button>
      </Card>
      {d.uses.map((u) => (
        <Card key={u.id} testid={`use-${u.id}`} className={u.active ? "" : "opacity-50"}>
          <div className="flex justify-between">
            <p className="text-sm font-semibold">{u.active_substance} · {u.adversity_name}</p>
            {u.active && <button onClick={async () => { await api.delete(`/pro/verified-uses/${u.id}`); load(); }} data-testid={`use-disable-${u.id}`}><Trash2 size={14} /></button>}
          </div>
          <p className="text-xs text-[#5b6b60]">{u.crop} · {MEANS[u.means_type]}{u.organic_allowed && " · ammesso in bio"} · verificato il {fmtDate(u.verified_at)}</p>
          {u.registry_check?.verified && <p className="text-[11px] text-[#8a958d]">Registro: {u.registry_check.authorized_products} prodotti autorizzati</p>}
        </Card>
      ))}
    </div>
  );
}
